<script setup lang="ts">
import type { TData } from '~/types/rating'
import { showModal } from '~/store/modal/AuditModal'

interface Props {
  isLoading: boolean
  organizationId?: number
  data: TData
  isOrdered?: boolean
  showTop?: boolean
  canPaginate?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  data: () => ({
    data: [],
  }),
  showTop: true,
  organizationId: -1,
  isOrdered: true,
  canPaginate: true,
})
defineEmits(['switchOrder', 'nextPage'])

const { $gtm } = useNuxtApp()
const top = ref([])
const countOfElements = computed(() => {
  return Number(props.data.meta.total) - Number(props.data.meta.to) >= 50 ? 50 : Number(props.data.meta.total) - Number(props.data.meta.to)
})

function showAudit() {
  showModal('')
  $gtm.sendAnalytics(
    'audit_rating_sidebar_form_open',
    'click',
    'Audit',
    'Заказать',
  )
}

const stop = watch(toRef(props), () => {
  if (props.data.meta && props.data.meta.total) {
    for (let percent = 0; percent < 100; percent++)
      top.value.push({ index: Math.round((props.data.meta.total / 100) * percent), percent: percent + 1 })
    stop()
  }
})
</script>

<template>
  <div
    class="flex flex-col md:flex-row gap-10 show"
  >
    <div v-if="props.data.data" class="w-full md:w-2/3 overflow-hidden">
      <table v-if="props.data.data.length > 0" class="w-full duration-150" :class="{ 'opacity-50': props.isLoading }">
        <thead class="relative">
          <tr>
            <th>
              <p v-if="props.data.data[0].organizations && showTop" class="ml-1 text-left text-m-primary-large md:text-h3 md:hidden">
                ТОП 1% из {{ props.data.meta.total }}
              </p>
            </th>
            <th class="relative">
              <p v-if="props.data.data[0].organizations && showTop" class="absolute top-0 -left-8 text-left text-m-primary-large md:text-h3 md:block hidden">
                ТОП 1% из {{ props.data.meta.total }}
              </p>
            </th>
            <th class="text-primary py-3 hidden" />
            <th
              class="text-m-primary md:text-primary text-end"
            >
              <div class="w-full md:w-fit flex justify-end">
                <p
                  class="border-b-[1px] w-fit"
                  :class="{
                    'text-blue-500 border-blue-400 border-dashed cursor-pointer hover:border-blue-300 duration-150 ease-out': isOrdered,
                    'text-black border-white': !isOrdered,
                  }"
                  @click="isOrdered ? $emit('switchOrder') : null"
                >
                  Оценка
                </p>
              </div>
            </th>
            <th
              class="text-m-primary md:text-primary pl-3 pr-4 duration-150"
            >
              <div class="w-full flex justify-end">
                <p
                  class="border-b-[1px] w-fit"
                  :class="{
                    'text-blue-500 border-blue-400 border-dashed cursor-pointer hover:border-blue-300 duration-150 ease-out': !isOrdered,
                    'text-black border-white': isOrdered,
                  }"
                  @click="!isOrdered ? $emit('switchOrder') : null"
                >
                  Отзывы
                </p>
              </div>
            </th>
          </tr>
        </thead>

        <tbody v-if="props.data.data[0].organizations">
          <template v-for="(topData, idx) in props.data.data">
            <tr v-if="idx !== 0 && topData.name && showTop" :key="idx" class="my-4 text-xl">
              <td colspan="3">
                <p class="text-m-primary-large md:text-h3 ml-1 mt-4 md:ml-1">
                  ТОП {{ topData.percent }}% из {{ props.data.meta.total }}
                </p>
              </td>
            </tr>

            <template
              v-for="(item, index) in topData.organizations"
              :key="index"
            >
              <tr v-if="index % 20 === 19">
                <td colspan="4">
                  <div class="w-full self-start block md:hidden my-1">
                    <div class="flex flex-col gap-7">
                      <div class="bg-blue-500 rounded-xl text-white show">
                        <div class="pl-5 pt-8 pr-6 pb-14">
                          <h5>Аудит карточки на картах</h5>
                          <p class="mt-2">
                            Проведем бесплатный аудит ваших филиалов в картах совместно с аналитиком Ранга, определим слабые места и точки роста.
                          </p>
                          <RButton class="text-black mt-6" @click="showAudit">
                            Заказать
                          </RButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <tr v-if="idx === 0" class="h-1" />

              <tr
                class="show rounded-lg hover:bg-yellow-300 duration-100 ease-out"
              >
                <td class="p-2 table-cell md:hidden rounded-l">
                  <p class="text-m-primary-small md:text-base">
                    {{ item.place_in_rating }}
                  </p>
                  <div>
                    <NuxtLink
                      :to="`/rating/org/${item.slug}`"
                    >
                      <span class="cursor-pointer text-blue-500 border-b-[1px] border-blue-400 hover:border-blue-300 duration-150 ease-out">{{ item.name }}</span>
                      <span v-if="item.is_network" class="text-black no-underline">{{ item.metro ? `, ${item.metro}` : null }}</span>
                    </NuxtLink>
                  </div>
                </td>

                <!-- Отдельные столбцы для десктопной версии -->
                <td
                  class="p-2 md:table-cell hidden rounded-l"
                >
                  {{ item.place_in_rating }}
                </td>
                <td class="py-2 hidden md:block min-w-[400px]">
                  <NuxtLink
                    :to="`/rating/org/${item.slug}`"
                  >
                    <span class="cursor-pointer text-blue-500 border-b-[1px] border-blue-400 hover:border-blue-300 duration-150 ease-out">{{ item.name }}</span>
                    <span v-if="item.is_network" class="text-black no-underline">{{ item.metro ? `, ${item.metro}` : null }}</span>
                  </NuxtLink>
                </td>
                <td class="md:hidden" />
                <td class="py-2 text-end">
                  {{ item.rating_value.toFixed(1) }}
                </td>
                <td
                  class="p-2 pr-4 text-end rounded-r"
                >
                  {{ item.reviews_count }}
                </td>
              </tr>
            </template>
          </template>
        </tbody>
        <tbody v-else>
          <template
            v-for="(item, index) in props.data.data"
            :key="index"
          >
            <tr v-if="index % 20 === 19">
              <td colspan="4">
                <div class="w-full self-start block md:hidden my-1">
                  <div class="flex flex-col gap-7">
                    <div class="bg-blue-500 rounded-xl text-white show">
                      <div class="pl-5 pt-8 pr-6 pb-14">
                        <h5>Аудит карточки на картах</h5>
                        <p class="mt-2">
                          Проведем бесплатный аудит ваших филиалов в картах совместно с аналитиком Ранга, определим слабые места и точки роста.
                        </p>
                        <RButton class="text-black mt-6" @click="showModal('')">
                          Заказать
                        </RButton>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            <tr
              class="show rounded-lg hover:bg-yellow-300 duration-100 ease-out"
            >
              <td class="p-2 table-cell md:hidden rounded-l">
                <p class="text-m-primary-small md:text-base">
                  {{ item.place_in_rating }}
                </p>
                <div>
                  <NuxtLink
                    :to="`/rating/org/${item.slug}`"
                  >
                    <span class="cursor-pointer text-blue-500 border-b-[1px] border-blue-400 hover:border-blue-300 duration-150 ease-out">{{ item.name }}</span>
                    <span v-if="item.is_network" class="text-black no-underline">{{ item.metro ? `, ${item.metro}` : null }}</span>
                  </NuxtLink>
                </div>
              </td>

              <!-- Отдельные столбцы для десктопной версии -->
              <td
                class="p-2 md:table-cell hidden rounded-l"
              >
                {{ item.place_in_rating }}
              </td>
              <td class="py-2 hidden md:block min-w-[400px]">
                <NuxtLink
                  :to="`/rating/org/${item.slug}`"
                >
                  <span class="cursor-pointer text-blue-500 border-b-[1px] border-blue-400 hover:border-blue-300 duration-150 ease-out">{{ item.name }}</span>
                  <span v-if="item.is_network" class="text-black no-underline">{{ item.metro ? `, ${item.metro}` : null }}</span>
                </NuxtLink>
              </td>
              <td class="md:hidden" />
              <td class="py-2 text-end">
                {{ item.rating_value.toFixed(1) }}
              </td>
              <td
                class="p-2 pr-4 text-end rounded-r"
              >
                {{ item.reviews_count }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <p
        v-else
        class="text-2xl font-bold text-white bg-[#383D44] py-10 rounded-md text-center"
      >
        Данные отсутствуют
      </p>
      <template v-if="props.data.meta">
        <div
          v-if="props.data.meta.total > 50"
          class="flex flex-col md:flex-row gap-1 md:gap-7 items-start md:items-center mt-8 px-3"
        >
          <div
            v-if="canPaginate"
            class="group flex justify-center items-center md:text-primary text-m-primary text-nowrap text-blue-500 cursor-pointer hover:text-blue-400 duration-150"
            @click="isLoading ? null : $emit('nextPage')"
          >
            <p class="w-52 md:w-[130px] md:text-left">
              Показать еще<br class="hidden md:block"> {{ countOfElements }} из {{ props.data.meta.total }}
            </p>
            <Icon class="text-blue-500 group-hover:text-blue-400 duration-150" name="i-mdi-keyboard-arrow-down" />
          </div>
          <div class="text-primary-small">
            Все данные из Яндекс.Карт. Если кого-то не хватает или не можете найти
            — пишите на
            <NuxtLink
              class="text-blue-500 cursor-pointer"
              to="mailto:art@rang.ai"
            >
              <span class="border-b border-blue-300">art@rang.ai</span>
            </NuxtLink>
          </div>
        </div>
      </template>
    </div>
    <div v-if="props.data.data" class="hidden md:block md:w-1/3 sticky self-start">
      <div class="flex flex-col gap-7">
        <div class="bg-blue-500 rounded-xl text-white show">
          <div class="pl-5 pt-8 pr-6 pb-14">
            <h5>Аудит карточки на картах</h5>
            <p class="mt-2">
              Проведем бесплатный аудит ваших филиалов в картах совместно с аналитиком Ранга, определим слабые места и точки роста.
            </p>
            <RButton class="text-black mt-6" @click="showAudit">
              Заказать
            </RButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@keyframes show {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.show {
  animation: show 200ms ease-in-out;
}
</style>
